import { Box } from '@mui/material';
import { PostProcessingIcon, RingIcon } from '~/assets/workspace-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { ContourEditBlocker } from '~/domain/dicom/contourEditBlocker';
import { editBlockedReasonId } from '~/components/Structures/editBlockedMessage';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import {
    operationPanelState,
    PostProcessingTab,
} from '~/state/atoms/toolsAtoms';
import PostProcessingMenu from './PostProcessingMenu';

/** Gates a selected-ROI tool reports on when disabled. */
interface SelectedRoiToolGates {
    /** A concrete ROI is selected. */
    hasSelectedRoi: boolean;
    isPointRoi: boolean;
    /** Which guard blocks this particular tool, or undefined when none does. */
    editBlocker: ContourEditBlocker | undefined;
    isBulkSelectionActive: boolean;
}

/**
 * The i18n id of the reason a selected-ROI tool cannot be started, in the
 * order the user would fix them, or undefined when it can. A disabled tool
 * with no reason is a dead button the user cannot tell from a defect.
 */
function selectedRoiToolDisabledReasonId(
    gates: SelectedRoiToolGates
): string | undefined {
    if (!gates.hasSelectedRoi) {
        return 'tool.disabled.noRoi';
    }
    if (gates.isPointRoi) {
        return 'tool.disabled.pointRoi';
    }
    if (gates.editBlocker) {
        return editBlockedReasonId(gates.editBlocker);
    }
    if (gates.isBulkSelectionActive) {
        return 'tool.disabled.bulkSelection';
    }
    return undefined;
}

// ── Main section ────────────────────────────────────────────────────

export default function PostProcessingSection() {
    const {
        isPointRoi,
        isStructureSetApproved,
        selectionEditBlocker,
        isBulkSelectionActive,
        hasSelectedRoi,
        targetUnresolved,
        isPrimaryImageIncomplete,
        imageIncompleteReason,
    } = useStructureToolGating();
    const intl = useIntl();
    const [panelState, setPanelState] = useRecoilState(operationPanelState);
    const noRoiSelected = !hasSelectedRoi || isPointRoi;

    // Post-processing rewrites the selected structure's geometry, so both
    // guards on it bind — approval and the edit lock. Ring writes a NEW ROI
    // instead, so neither guard on the source blocks it; an approved
    // structure set, which rejects new ROIs, does.
    //
    // A partially streamed image refuses all of them: slices that have not
    // arrived yet cannot be rewritten.
    //
    // An unresolved edit target comes first: nothing is armed at all, and it
    // can coincide with a state that would otherwise name the wrong cause.
    const disabledReasonFor = (
        editBlocker: ContourEditBlocker | undefined
    ): string | undefined => {
        if (targetUnresolved) {
            return intl.formatMessage({
                id: 'tool.disabled.gridTargetUnresolved',
            });
        }
        if (isPrimaryImageIncomplete) {
            return imageIncompleteReason;
        }
        const reasonId = selectedRoiToolDisabledReasonId({
            hasSelectedRoi,
            isPointRoi,
            editBlocker,
            isBulkSelectionActive,
        });
        return reasonId === undefined
            ? undefined
            : intl.formatMessage({ id: reasonId });
    };

    const postProcessingDisabledReason =
        disabledReasonFor(selectionEditBlocker);
    const ringDisabledReason = disabledReasonFor(
        isStructureSetApproved ? 'approval' : undefined
    );

    // Derived from the reason, never stated separately: a button can then
    // not be greyed out in a state that has nothing to say about it.
    const isPostProcessingDisabled = postProcessingDisabledReason !== undefined;

    const postProcessingButtonRef = useRef<HTMLElement | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const handleRingToggle = useCallback(() => {
        setPanelState((prev) =>
            prev.type === 'ring' ? { type: 'closed' } : { type: 'ring' }
        );
    }, [setPanelState]);

    /**
     * Closed, the button expands into the operation list rather than
     * opening the panel on whichever operation was last used — reaching
     * Crop otherwise meant opening on Smooth and then changing tab.
     *
     * Open, it closes the panel. That keeps the toggle the button has
     * always had: a second press putting the panel away is the thing a
     * user has learned about it, and switching operation from here would
     * be a third meaning for the same press when the tabs already do it.
     */
    const handlePostProcessingClick = useCallback(() => {
        if (panelState.type === 'post-processing') {
            setPanelState({ type: 'closed' });
            return;
        }
        setMenuAnchor(postProcessingButtonRef.current);
    }, [panelState.type, setPanelState]);

    const handlePickOperation = useCallback(
        (tab: PostProcessingTab) => {
            setMenuAnchor(null);
            setPanelState({ type: 'post-processing', tab });
        },
        [setPanelState]
    );

    // The panel is unreachable the moment the button is, so an operation
    // list left standing would open a panel that is about to be closed
    // from under it.
    useEffect(() => {
        if (isPostProcessingDisabled) {
            setMenuAnchor(null);
        }
    }, [isPostProcessingDisabled]);

    // Ring writes a new ROI, so source-ROI approval doesn't block it; only
    // a missing ROI selection, an approved structure set, or an unresolved
    // edit target does.
    useEffect(() => {
        if (
            (noRoiSelected ||
                isStructureSetApproved ||
                isPrimaryImageIncomplete ||
                targetUnresolved) &&
            panelState.type === 'ring'
        ) {
            setPanelState({ type: 'closed' });
        }
    }, [
        noRoiSelected,
        isStructureSetApproved,
        isPrimaryImageIncomplete,
        targetUnresolved,
        panelState.type,
        setPanelState,
    ]);

    // Disabling the ToolbarButton doesn't close an already-open panel.
    useEffect(() => {
        if (panelState.type === 'post-processing' && isPostProcessingDisabled) {
            setPanelState({ type: 'closed' });
        }
    }, [panelState.type, isPostProcessingDisabled, setPanelState]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            {/* Entry point for the Post Processing panel. The button
                expands into the operation list; the panel's own tabs
                switch between them once it is open. */}
            <ToolbarButton
                icon={<PostProcessingIcon />}
                data-testid="post-processing-button"
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({
                        id: 'toolbar.postProcessing.tooltip',
                    }),
                    postProcessingDisabledReason
                )}
                tooltipPlacement="left"
                selected={
                    panelState.type === 'post-processing' || menuAnchor !== null
                }
                disabled={isPostProcessingDisabled}
                onClick={handlePostProcessingClick}
                anchorRef={postProcessingButtonRef}
            />
            <PostProcessingMenu
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                onPick={handlePickOperation}
            />

            <ToolbarButton
                icon={<RingIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({ id: 'toolbar.ring.tooltip' }),
                    ringDisabledReason
                )}
                tooltipPlacement="left"
                selected={panelState.type === 'ring'}
                disabled={ringDisabledReason !== undefined}
                onClick={handleRingToggle}
            />
        </Box>
    );
}
